import isEmpty from 'lodash/isEmpty';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { parseOfferingResponse } from 'stores/offeringUtils';
import {
  Envelope,
  IAggBucket,
  IAggRequest,
  IFilter,
  IFilterOptionsItem,
  ILocationFilter,
  IOfferingBrowsingData
} from 'utils/globalTypes';
import { safeRunRequest } from 'utils/networkRequests';
import { getObjProperty } from 'utils/object';
import { MAX_PAGE_SIZE, removeSingleFilterValue } from 'utils/query';

interface IAggsMapper {
  [key: string]: IAggBucket[];
}

class MarketplaceStore {
  offerings: IOfferingBrowsingData[] = [];
  allOfferings: IOfferingBrowsingData[] = [];
  filterOptions: IFilterOptionsItem[] = [];
  filters: IFilter[] = [];
  locationFilter: ILocationFilter = {};
  offeringSearchTerm = '';
  currentSearch?: string = '';

  constructor() {
    makeObservable(this, {
      offerings: observable,
      allOfferings: observable,
      filterOptions: observable,
      filters: observable,
      locationFilter: observable,
      currentSearch: observable,

      setAssets: action,
      clearFilters: action,
      runStatsQuery: action,
      setOfferingSearchTerm: action,
      addFilter: action,
      removeFilter: action,
      setFilters: action,
      setFilterOptions: action,
      setLocationFilter: action,
      clearLocationFilter: action,
      setSelected: action,
      keepActive: action,
      clearFilter: action,
      clearStore: action,

      getFilterOptions: computed,
      getFilters: computed,
      getLocationFilter: computed,
      getAssets: computed,
      getAssetsLength: computed,
      getIsFiltered: computed,
      getOfferingSearchTerm: computed
    });
  }

  get getAssets() {
    return this.offerings;
  }

  get getFilterOptions() {
    return this.filterOptions;
  }

  get getFilters() {
    return this.filters;
  }

  get getAssetsLength() {
    return this.offerings && this.offerings.length;
  }

  get getLocationFilter() {
    return this.locationFilter;
  }

  get getOfferingSearchTerm() {
    return this.offeringSearchTerm;
  }

  get getIsFiltered() {
    return !isEmpty(this.filters) || !isEmpty(this.locationFilter);
  }

  clearFilters() {
    this.filters = [];
    this.locationFilter = {};
  }

  runStatsQuery(defaultFilterOptions?: IFilterOptionsItem[], aggregations?: IAggRequest) {
    const queryBody = {
      offering_filter: this.offeringSearchTerm,
      ...(aggregations && { aggregations: aggregations }),
      limit: 0
    };

    return safeRunRequest('statsOfferings', queryBody).then((json) => {
      const { aggregations: aggregationsData } = json;

      runInAction(() => {
        this.filterOptions =
          aggregationsData && defaultFilterOptions
            ? this.filterOptionsMapper(defaultFilterOptions, aggregationsData)
            : defaultFilterOptions || [];

        this.filters
          .filter((filter) => filter.field && filter.value)
          .map((filter) => {
            filter.value &&
              filter.value.map((value) => this.setSelected(true, filter.field, value));
          });
      });
    });
  }

  async setAssets(requestCmd?: string, size?: number, append?: boolean) {
    const queryBody = {
      offering_filter: this.offeringSearchTerm
    };

    const response = await safeRunRequest(requestCmd || 'searchOfferings', queryBody);
    const queryItems = response.data;

    runInAction(() => {
      if (append) {
        const newAssets = parseOfferingResponse(queryItems);
        this.offerings = queryItems ? this.offerings.concat(newAssets) : [];
      } else {
        this.offerings = parseOfferingResponse(queryItems);
      }

      this.currentSearch = this.offeringSearchTerm;
      this.keepActive();
    });

    return response;
  }

  async setAllAssets(requestCmd?: string) {
    const queryBody = {
      offering_filter: this.offeringSearchTerm,
      limit: MAX_PAGE_SIZE
    };

    const response = await safeRunRequest(requestCmd || 'searchOfferings', queryBody);
    const queryItems = response.data;

    runInAction(() => {
      this.allOfferings = parseOfferingResponse(queryItems);
      this.keepActive();
    });
  }

  filterOptionsMapper(filters: IFilterOptionsItem[], aggsData: IAggsMapper) {
    return filters.map(({ filterType, keyName, filterValues }) => {
      const aggData = getObjProperty(aggsData, keyName);
      return aggData
        ? {
            filterType,
            keyName: keyName,
            filterValues: aggData?.map((sp) => {
              return {
                key: sp?.key,
                name: sp?.name,
                from: sp?.from,
                to: sp?.to,
                count: sp?.count
              };
            })
          }
        : { filterType, keyName, filterValues };
    });
  }

  setOfferingSearchTerm(searchTerm: string) {
    this.offeringSearchTerm = searchTerm;
  }

  setFilters(filters?: IFilter[]) {
    this.filters = filters || [];
  }

  setFilterOptions(filterOptions?: IFilterOptionsItem[]) {
    this.filterOptions = filterOptions || [];
  }

  addFilter(filterField: string, filterValue: string) {
    this.setSelected(true, filterField, filterValue);

    const existingFilter = this.filters.find((object) => {
      return object.field === filterField;
    });

    const allOtherFilters = this.filters.filter((object) => {
      return object.field !== filterField;
    });

    const existingFilterValues = existingFilter?.value || [];
    const newFilterValues = [...existingFilterValues, filterValue || ''];

    this.filters = [...allOtherFilters, { field: filterField, value: newFilterValues }];

    return this.filters;
  }

  removeFilter(filterField = '', filterValue = '') {
    this.setSelected(false, filterField, filterValue);

    if (!filterValue) {
      this.filters = this.filters.filter((filter) => {
        return !(filter.field === filterField);
      });
    } else if (filterValue) {
      this.filters = removeSingleFilterValue(this.filters, filterField, filterValue);
    } else {
      this.filters = this.filters.filter((filter) => {
        return filter.field !== filterField;
      });
    }

    return this.filters;
  }

  clearFilter(selectedFilterType?: string, keyName?: string) {
    this.filters = this.filters.filter((filter) => {
      return filter.field !== keyName;
    });

    this.filterOptions = this.getFilterOptions.map((list) => {
      if (list.filterType === selectedFilterType) {
        return {
          ...list,
          filterValues: list.filterValues?.map((element) => {
            return { ...element, isSelected: false };
          })
        };
      }

      return list;
    });
  }

  setSelected(status: boolean, selectedField: string, selectedValue: string) {
    this.filterOptions = this.getFilterOptions.map((list) => {
      if (list.keyName === selectedField) {
        return {
          ...list,
          filterValues: list.filterValues?.map((element) => {
            if (element.from || element.to) {
              const [from, to] = selectedValue && selectedValue.split('-');

              if (element.from == from && element.to == to) {
                return { ...element, isSelected: status };
              }
            }

            if (element.key === selectedValue || element.name == selectedValue) {
              return { ...element, isSelected: status };
            }

            return element;
          })
        };
      }

      return list;
    });
  }

  keepActive() {
    this.filters.map((filter) => {
      return (this.filterOptions = this.getFilterOptions.map((list) => {
        return {
          ...list,
          filterValues: list.filterValues?.map((element) => {
            if (list.keyName === filter.field) {
              const isSelected = filter.value && filter.value?.includes(element.key);
              return {
                ...element,
                isSelected
              };
            }

            return element;
          })
        };
      }));
    });
  }

  setLocationFilter(fieldName: string, envelope: Envelope) {
    this.locationFilter = { field: fieldName, value: envelope };
  }

  clearLocationFilter() {
    this.locationFilter = {};
  }

  clearStore() {
    this.offerings = [];
    this.allOfferings = [];
    this.offeringSearchTerm = '';
    this.clearFilters();
  }
}

export default MarketplaceStore;
